import { Field } from '@/components/form';
import { Input } from '@/components/ui/input';
import { rateLine, trimRate, useRateOn } from '@/lib/currencies';
import { formatDate } from '@/lib/format';
import { formatMinor } from '@/lib/money';
import { ltr } from '@/lib/arabic';
import { useT } from '@/lib/i18n';

/**
 * Exchange rate on a foreign-currency transaction (document 05 pattern 3; QBO shows
 * "1 USD = [3.6725] AED" under the header). Empty means "the rate on the date from Currencies",
 * shown as the placeholder; typing a rate overrides it. Returns nothing in the home currency.
 */
export function ExchangeRateField({
  currency,
  home,
  date,
  value,
  onChange,
  disabled,
  error,
  className,
}: {
  currency: string;
  home: string;
  date: string;
  value: string;
  onChange: (rate: string) => void;
  disabled?: boolean;
  error?: string | undefined;
  className?: string;
}) {
  const t = useT();
  const day = useRateOn(currency, date, currency !== home);
  if (!home || currency === home) return null;
  const auto = day.data?.rate ?? null;
  const hint = value
    ? t('settingsPages.general.currency.rateEntered')
    : auto
      ? t('settingsPages.general.currency.rateFrom', { date: ltr(formatDate(day.data!.rateDate!)) })
      : day.isSuccess
        ? t('settingsPages.general.currency.noRate', { code: ltr(currency) })
        : undefined;
  return (
    <Field
      label={t('settingsPages.general.currency.exchangeRate')}
      error={error}
      hint={hint}
      className={className}
    >
      {(id, describedBy) => (
        <div className="flex items-center gap-2 text-sm">
          <span className="whitespace-nowrap text-muted-foreground">1 {currency} =</span>
          <Input
            id={id}
            className="tnum w-28 text-end"
            inputMode="decimal"
            aria-describedby={describedBy}
            aria-invalid={Boolean(error) || undefined}
            value={value}
            placeholder={auto ? trimRate(auto) : ''}
            disabled={disabled}
            data-testid="fx-rate"
            onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
          />
          <span className="text-muted-foreground">{home}</span>
        </div>
      )}
    </Field>
  );
}

/** The rate a form shows and converts with: the typed one, else the day's, else the saved one. */
export function useEffectiveRate(
  currency: string,
  home: string,
  date: string,
  typed: string,
  saved?: string | null,
): string | null {
  const day = useRateOn(currency, date, currency !== home && !typed);
  if (currency === home) return '1';
  if (typed) return typed;
  return day.data?.rate ?? saved ?? null;
}

/** "Total (AED) 3,856.13" lines under a foreign document's totals, with the rate. */
export function HomeTotals({
  currency,
  home,
  rate,
  rows,
}: {
  currency: string;
  home: string;
  rate: string | null;
  rows: Array<{ label: string; minor: bigint | null; testId?: string }>;
}) {
  const t = useT();
  if (!home || currency === home) return null;
  return (
    <>
      <dt className="col-span-2 border-t pt-2 text-xs text-muted-foreground" data-testid="fx-line">
        {rate
          ? rateLine(currency, rate, home)
          : t('settingsPages.general.currency.enterRate', { code: currency })}
      </dt>
      {rows.map((r) => (
        <div key={r.label} className="contents">
          <dt className="text-muted-foreground">
            {r.label} ({home})
          </dt>
          <dd className="tnum text-end" data-testid={r.testId}>
            {r.minor === null || !rate ? '—' : `${home} ${formatMinor(r.minor)}`}
          </dd>
        </div>
      ))}
    </>
  );
}

/** Small chip with a currency code next to a name (QBO shows "USD" by a foreign customer). */
export function CurrencyChip({ code }: { code: string }) {
  return (
    <span
      className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground"
      data-testid="currency-chip"
    >
      {code}
    </span>
  );
}
